import React, { useState, useEffect, useRef } from 'react';
import Button from './Button';
import { IconPencil, IconX } from '@tabler/icons-react';
import api from '../services/api';
import { parseDateInput, isoToDisplay, todayDisplay, DateInputField } from '../utils/dateInput';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatVisitDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

function formatAuditDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Add mode opens with a BLANK date. Today's visits are normally captured via
// the banner's "Seen Today" button, so a modal entry is more likely a past
// date; the Today button stays as a quick backup for the today case.
const EMPTY_FORM = {
  visit_date: '',
  is_consult: false,
};

// ---------------------------------------------------------------------------
// VisitHistoryModal
//
// Props:
//   patient      — full patient object
//   resolveUser  — fn(userId) => 'First Last'
//   onClose      — callback to close modal
//   onSaved      — callback after any write — triggers parent reload
// ---------------------------------------------------------------------------
export default function VisitHistoryModal({ patient, resolveUser, onClose, onSaved }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [consultWarning, setConsultWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  const isEditMode = editingId !== null;

  // ── Load visits on mount ──
  useEffect(() => {
    loadVisits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVisits = async () => {
    try {
      const res = await api.get(`/patients/${patient.id}/visits`);
      setVisits(res.data);
    } catch {
      setGlobalError('Failed to load visit history.');
    } finally {
      setLoading(false);
    }
  };

  // ── Derived ──
  const existingConsults = visits.filter(v => v.is_consult);

  // ── Form handlers ──
  const updateForm = (field, value) => {
    setFormError('');
    const updated = { ...form, [field]: value };

    // Consult warning logic
    if (field === 'is_consult' && value === true && existingConsults.length > 0) {
      const existing = existingConsults
        .filter(v => v.id !== editingId)
        .map(v => formatVisitDate(v.visit_date))
        .join(', ');
      if (existing) {
        setConsultWarning(
          `A consult date of ${existing} is already recorded. Mark this visit as an additional consult?`
        );
      }
    } else if (field === 'is_consult' && value === false) {
      setConsultWarning('');
    }

    setForm(updated);
  };

  const setToday = () => {
    setFormError('');
    setForm(f => ({ ...f, visit_date: todayDisplay() }));
  };

  // ── Drawer open/close ──
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setConsultWarning('');
    setConfirmDel(false);
    setDrawerOpen(true);
  };

  const openEdit = (visit) => {
    setEditingId(visit.id);
    setConsultWarning('');
    setForm({
      visit_date: isoToDisplay(visit.visit_date),
      is_consult: visit.is_consult,
    });
    setFormError('');
    setConfirmDel(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setConsultWarning('');
    setConfirmDel(false);
  };

  const validateForm = () => {
    if (!form.visit_date || !form.visit_date.trim()) {
      setFormError('Visit date is required.');
      return false;
    }
    const parsed = parseDateInput(form.visit_date);
    if (!parsed) {
      setFormError('Enter date as M/D/YY or M/D/YYYY (e.g. 5/15/26)');
      return false;
    }
    return true;
  };

  // ── Save ──
  const handleSave = async () => {
    if (!validateForm()) return;

    // Prevent duplicate visit dates in add mode
    if (!isEditMode) {
      const parsed = parseDateInput(form.visit_date);
      const duplicate = visits.some(v => v.visit_date === parsed?.iso);
      if (duplicate) {
        setFormError('A visit on this date already exists.');
        return;
      }
    }

    setSaving(true);
    setFormError('');
    setGlobalError('');

    const parsed = parseDateInput(form.visit_date);
    const payload = {
      visit_date: parsed.iso,
      is_consult: form.is_consult,
    };

    try {
      if (isEditMode) {
        await api.patch(`/patients/${patient.id}/visits/${editingId}`, payload);
      } else {
        await api.post(`/patients/${patient.id}/visits`, payload);
      }
      await loadVisits();
      onSaved();
      closeDrawer();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to save visit.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!editingId) return;
    setSaving(true);
    setGlobalError('');
    try {
      await api.delete(`/patients/${patient.id}/visits/${editingId}`);
      await loadVisits();
      onSaved();
      closeDrawer();
    } catch {
      setGlobalError('Failed to delete visit.');
    } finally {
      setSaving(false);
    }
  };

  // ── Enter key to save in add mode ──
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && !isEditMode) {
      e.preventDefault();
      handleSave();
    }
  };

  const editingVisit = isEditMode ? visits.find(v => v.id === editingId) : null;

  // ---------------------------------------------------------------------------
  // Drawer render
  // ---------------------------------------------------------------------------
  const renderDrawer = () => (
    <div style={{
      width: drawerOpen ? '270px' : '0px',
      opacity: drawerOpen ? 1 : 0,
      overflow: 'hidden',
      transition: 'width 0.25s ease, opacity 0.2s ease',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTop: `3px solid var(--color-accent-navy)`,
      border: drawerOpen ? '1px solid var(--color-border)' : 'none',
      borderTopWidth: drawerOpen ? '3px' : '0',
      borderTopColor: 'var(--color-accent-navy)',
      borderRadius: drawerOpen ? '0 0 var(--radius-sm) var(--radius-sm)' : '0',
      marginLeft: drawerOpen ? '16px' : '0',
      backgroundColor: 'var(--color-bg-subtle)',
    }}>

      {/* Drawer header */}
      <div style={styles.drawerHeader}>
        <span style={styles.drawerTitle}>
          {isEditMode ? 'Edit Visit' : 'Add Visit'}
        </span>
        <button style={styles.drawerCloseBtn} onClick={closeDrawer} aria-label="Close drawer">
          <IconX size={16} stroke={2} />
        </button>
      </div>

      {/* Drawer body */}
      <div style={styles.drawerBody}>

        {/* Date field */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>
            Visit Date <span style={styles.required}>*</span>
          </label>
          <div style={styles.dateRow}>
            <DateInputField
              value={form.visit_date}
              onChange={(display) => updateForm('visit_date', display)}
              error={formError && formError.toLowerCase().includes('date') ? formError : null}
              disabled={saving}
              onKeyDown={handleFormKeyDown}
              inputStyle={{ flex: 1 }}
              autoFocus={drawerOpen && !isEditMode}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={setToday}
              disabled={saving}
            >
              Today
            </Button>
          </div>
        </div>

        {/* Is Consult checkbox */}
        <div style={styles.fieldGroup}>
          <label style={styles.consultLabel}>
            <input
              type="checkbox"
              checked={form.is_consult}
              onChange={e => updateForm('is_consult', e.target.checked)}
              style={{ marginTop: '1px' }}
            />
            <div>
              <span style={styles.consultText}>Initial Consult</span>
              <span style={styles.consultHint}> — marks this as the first nephrology visit</span>
            </div>
          </label>
        </div>

        {/* Consult warning */}
        {consultWarning && (
          <div style={styles.warningBanner}>{consultWarning}</div>
        )}

        {/* Form error (non-date) */}
        {formError && !formError.toLowerCase().includes('date') && (
          <div style={styles.formError}>{formError}</div>
        )}

        {/* Audit line — edit mode only */}
        {isEditMode && editingVisit && (
          <div style={styles.auditLine}>
            {`Added by ${resolveUser(editingVisit.created_by)} on ${formatAuditDate(editingVisit.created_at)}`}
          </div>
        )}

      </div>

      {/* Drawer footer — Delete left, Cancel + Save right; swaps to confirm when deleting */}
      <div style={styles.drawerFooter}>
        {confirmDel ? (
          <div style={styles.confirmBox}>
            <div style={styles.confirmText}>
              This visit will be permanently deleted.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Confirm delete'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}>
            <div>
              {isEditMode && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmDel(true)}
                  disabled={saving}
                >
                  Delete
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" onClick={closeDrawer} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Table render
  // ---------------------------------------------------------------------------
  const renderTable = () => {
    if (loading) {
      return <div style={styles.emptyState}>Loading...</div>;
    }
    if (visits.length === 0) {
      return <div style={styles.emptyState}>No visits recorded.</div>;
    }

    return (
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, textAlign: 'left' }}>Visit Date</th>
              <th style={{ ...styles.th, width: '90px' }} />
              <th style={{ ...styles.th, width: '36px' }} />
            </tr>
          </thead>
          <tbody>
            {visits.map(v => {
              const isHovered = hoveredRow === v.id;
              const isEditing = editingId === v.id;
              return (
                <tr
                  key={v.id}
                  style={{
                    ...styles.tr,
                    backgroundColor: isEditing || isHovered
                      ? 'var(--color-accent-wash)'
                      : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredRow(v.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => isEditing ? closeDrawer() : openEdit(v)}
                >
                  <td style={{
                    ...styles.td,
                    textAlign: 'left',
                  }}>
                    {formatVisitDate(v.visit_date)}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    {v.is_consult && (
                      <span style={styles.consultBadge}>Consult</span>
                    )}
                  </td>
                  <td style={styles.tdPencil}>
                    {isHovered && (
                      <IconPencil size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)' }} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------
  const modalStyle = {
    ...styles.modal,
    width: drawerOpen ? '660px' : '440px',
  };

  return (
    <div style={styles.overlay}>
      <div style={modalStyle}>

        {/* ── Header ── */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Visit History</h2>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            <IconX size={18} stroke={2} />
          </button>
        </div>

        {/* ── Global error ── */}
        {globalError && (
          <div style={styles.errorBanner}>{globalError}</div>
        )}

        {/* ── Top bar — Add button ── */}
        <div style={styles.topBar}>
          {!drawerOpen && (
            <Button variant="primary" size="sm" onClick={openAdd}>
              + Add Visit
            </Button>
          )}
        </div>

        {/* ── Content area — drawer + table ── */}
        <div style={styles.contentArea}>
          {renderDrawer()}
          <div style={styles.tablePane}>
            {renderTable()}
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    maxWidth: '95vw',
    maxHeight: '90vh',
    transition: 'width 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-lg)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px 16px',
    borderBottom: '1px solid var(--color-border)',
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: '17px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    fontFamily: 'var(--font-family-base)',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    padding: '4px',
    display: 'flex',
  },
  errorBanner: {
    margin: '0 24px',
    marginTop: '12px',
    padding: '10px 14px',
    backgroundColor: 'var(--color-severity-high-bg)',
    border: '1px solid var(--color-severity-high)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px',
    color: 'var(--color-severity-high)',
    fontFamily: 'var(--font-family-base)',
    flexShrink: 0,
  },

  // ── Top bar ──
  // Right inset is 40px (24px modal padding + 16px) so the button's right edge
  // lines up with the table's right border, which is inset 16px inside the
  // 24px-padded content area.
  topBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '14px 40px 0 24px',
    flexShrink: 0,
  },

  // ── Content area ──
  contentArea: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    padding: '12px 24px 20px 24px',
    gap: '0',
    minHeight: '220px',
    maxHeight: '440px',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 24px',
    borderTop: '1px solid var(--color-border)',
    flexShrink: 0,
  },

  // ── Drawer ──
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-card)',
    flexShrink: 0,
  },
  drawerTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  drawerCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    borderRadius: '4px',
  },
  drawerBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  drawerFooter: {
    padding: '12px 16px',
    borderTop: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-card)',
    flexShrink: 0,
  },

  // ── Table ──
  tablePane: {
    flex: 1,
    overflowY: 'auto',
    paddingLeft: '0',
  },
  tableWrapper: {
    border: '1px solid var(--color-border)',
    borderTop: '3px solid var(--color-accent-navy)',
    borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
    marginLeft: '16px',
    marginRight: '16px',
    maxWidth: '360px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-base)',
  },
  th: {
    textAlign: 'center',
    padding: '8px 12px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
    backgroundColor: 'var(--color-subNav)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  tr: {
    borderBottom: '1px solid var(--color-border)',
    transition: 'background-color 0.15s ease',
  },
  td: {
    padding: '10px 12px',
    color: 'var(--color-text-primary)',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  tdPencil: {
    padding: '10px 8px',
    width: '36px',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  emptyState: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic',
    padding: '48px 24px',
    textAlign: 'center',
  },

  // ── Form ──
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-base)',
  },
  required: {
    color: 'var(--color-severity-high)',
  },
  dateRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  consultLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  consultText: {
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  consultHint: {
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'var(--font-family-base)',
  },
  warningBanner: {
    padding: '10px 12px',
    backgroundColor: 'var(--color-severity-mild-bg)',
    border: '1px solid var(--color-severity-mild)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-severity-mild)',
    fontFamily: 'var(--font-family-base)',
    lineHeight: '1.5',
  },
  formError: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-severity-high)',
    fontFamily: 'var(--font-family-base)',
  },
  auditLine: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic',
    paddingTop: '8px',
    borderTop: '1px solid var(--color-border)',
    marginTop: '4px',
  },
  confirmBox: {
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid var(--color-severity-high)',
    backgroundColor: 'var(--color-severity-high-bg)',
  },
  confirmText: {
    fontSize: '13px',
    color: 'var(--color-severity-high)',
    fontWeight: 600,
    marginBottom: '10px',
    fontFamily: 'var(--font-family-base)',
  },
  consultBadge: {
    fontSize: '11px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-primary)',
    backgroundColor: 'var(--color-accent-wash)',
    border: '1px solid var(--color-accent-tint)',
    borderRadius: 'var(--radius-sm)',
    padding: '2px 8px',
    fontFamily: 'var(--font-family-base)',
    whiteSpace: 'nowrap',
  },
};